import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { LoggerService } from 'app/services/logger.service';
import { Utils } from 'app/utils/utils';
import { AuditEventType, AuditService } from './storage-api/audit.service';

type HttpMutatingOperations = 'post' | 'put' | 'patch' | 'delete';

interface HttpMutatingOperationsContext {
  enabled: boolean;
  name?: AuditEventType;
  properties?: Record<string, string>;
}

const initialContext = {
  enabled: false,
};

export const HTTP_MOI_CTX = new HttpContextToken<HttpMutatingOperationsContext>(
  () => initialContext
);

export function WithAuditContext(
  name: AuditEventType,
  propertiesFn: (instance: any, args: any[]) => Record<string, unknown>
) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]) {
      // Dynamically detect the HttpClient instance in `this`
      const httpProp = Object.keys(this).find((key) => this[key] instanceof HttpClient);

      if (!httpProp) {
        throw new Error(
          `@WithAuditContext could not find an HttpClient instance in ${target.constructor.name}`
        );
      }

      const originalHttp: HttpClient = this[httpProp];

      // build context values
      let properties: Record<string, string> | null = null;
      try {
        properties = Utils.stringifyRecordValues(propertiesFn(this, args));
      } catch (e) {
        console.error(
          `@WithAuditContext could not build context values for ${target.constructor.name}.${propertyKey}`,
          e
        );
        // Execute original method untouched
        return originalMethod.apply(this, args);
      }

      // Create a proxy to intercept HttpClient method calls
      const proxiedHttp = new Proxy(originalHttp, {
        get(httpTarget, prop: keyof HttpClient) {
          const orig = httpTarget[prop];
          if (typeof orig !== 'function') {
            return orig;
          }

          const method = prop.toString().toLowerCase() as HttpMutatingOperations;
          const shouldWrap = ['post', 'put', 'patch', 'delete'].includes(method);

          if (!shouldWrap) {
            // leave GET, HEAD, OPTIONS, etc. untouched
            return orig.bind(httpTarget);
          }

          // wrap every http call
          return function (...httpArgs: any[]) {
            let url: string;
            let body: any;
            let options: any;

            if (method === 'delete') {
              [url, options] = httpArgs;
            } else {
              [url, body, options] = httpArgs;
            }

            // Ensure options object exists
            options = options || {};

            // Merge or create HttpContext
            const ctx: HttpContext =
              options.context instanceof HttpContext ? options.context : new HttpContext();

            // apply context values
            ctx.set(HTTP_MOI_CTX, {
              enabled: true,
              name: name,
              properties: properties,
            });

            options.context = ctx;

            // Call the real HttpClient method
            if (method === 'delete') {
              return orig.call(httpTarget, url, options);
            }
            return orig.call(httpTarget, url, body, options);
          };
        },
      });

      // temporarily replace
      this[httpProp] = proxiedHttp;
      try {
        return originalMethod.apply(this, args);
      } finally {
        // restore
        this[httpProp] = originalHttp;
      }
    };

    return descriptor;
  };
}

@Injectable()
export class HttpMutatingOperationsInterceptor implements HttpInterceptor {
  constructor(
    private auditService: AuditService,
    private logger: LoggerService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ctx = req.context.get(HTTP_MOI_CTX);

    if (!ctx?.enabled || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            // Run async logging without blocking
            this.auditService
              .logEvent(ctx.name, ctx.properties)
              .catch((error) => this.logger.error('Unexpected error during logging', { error }));
          }
        },
      })
    );
  }
}
